import React, { useEffect, useState } from 'react';
import { useGame } from '../context/GameContext';
import axios from 'axios';
import {
  Trophy, Play, Hash, Calendar, Volume2, VolumeX, User, Coins, Users, Shuffle,
  Radio, Store, ChevronDown, ChevronUp, BookOpen, Star, Award, Settings, HelpCircle, Sparkles, Skull,
} from 'lucide-react';
import { AvatarChip } from '../components/Avatar';
import PartyLiveTicker from '../components/PartyLiveTicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// v3.6 CLEAN MENU: rainbow section palette — one hue per grouping so nothing is bland.
const SECTION_HUES = {
  perks:       '#00e5ff', // cyan
  codex:       '#c084fc', // purple
  avatar:      '#ff8a3d', // orange
  mastery:     '#ffe74a', // gold-yellow
  achievements:'#7cf5a8', // mint
  draft:       '#ff2ea6', // magenta
  weekly:      '#5ee7ff', // ice
  marketplace: '#ff007f', // hot pink
  bounties:    '#ffd700', // gold
  leaderboard: '#39ff14', // neon green
  cursed:      '#ff2b2b', // red — v3.13 shortcut to cursed runs
  settings:    '#a0a0a0', // dim grey
  howto:       '#ff9d5c', // peach
};

// Module-scope SectionBtn to avoid remount on every menu render (see React docs on component identity).
const SectionBtn = ({ hue, testid, onClick, icon: Icon, label }) => (
  <button
    className="brutal-btn text-xs flex items-center justify-center gap-1.5 py-2.5 menu-section-btn"
    onClick={onClick}
    data-testid={testid}
    style={{ borderColor: hue, color: hue, boxShadow: `4px 4px 0 ${hue}`, ['--hue']: hue }}
  >
    <Icon size={14}/> {label}
  </button>
);

export default function Menu() {
  const {
    startQuickRun, beginClassSelect, setScreen, playerName, setPlayerName, muted, toggleMute,
    profile, streamerMode, toggleStreamerMode,
    twitchChannel, twitchStatus, connectTwitch, disconnectTwitch,
    hasSavedRun, continueSavedRun, discardSavedRun,
    runStreak, bestStreak,
    setLeaderboardInitialTab,
  } = useGame();
  const [codeInput, setCodeInput] = useState('');
  const [topScore, setTopScore] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [twitchInput, setTwitchInput] = useState(twitchChannel || '');
  const [twitchPanelOpen, setTwitchPanelOpen] = useState(false);
  // v3.11 QoL: live curse-stack preview when typing a run code
  const [codeStack, setCodeStack] = useState(null); // { count, curse_ids }
  const [codeStackLoading, setCodeStackLoading] = useState(false);
  // v3.12 RETENTION: top rival + their beatable seed for one-click revenge
  const [nemesis, setNemesis] = useState(null); // { tag, best_score, losses, seed, avatar }

  useEffect(() => {
    axios.get(`${API}/leaderboard?limit=1`).then(r => {
      if (r.data && r.data.length) setTopScore(r.data[0]);
    }).catch(() => {});
  }, []);

  // v3.12 RETENTION: fetch top rival (someone who beat you on a shared seed) — one-click AVENGE.
  useEffect(() => {
    const tag = (playerName || '').trim().toUpperCase();
    if (!tag) { setNemesis(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/profile/${encodeURIComponent(tag)}/rivals?limit=20`);
        const rivals = (r.data && r.data.rivals) || [];
        // Find the rival who has BEATEN you the most (highest 'losses' from your POV → biggest threat).
        const enemy = rivals
          .filter(rv => (rv.losses || 0) > 0)
          .sort((a, b) => (b.losses - a.losses) || (b.best_score - a.best_score))[0];
        if (cancelled) return;
        if (!enemy) { setNemesis(null); return; }
        // Look up ONE of your shared seeds where they beat you, so AVENGE can jump straight in.
        try {
          const sr = await axios.get(`${API}/leaderboard?limit=100`);
          const rows = (sr.data || []).filter(row => row.player_name === enemy.tag);
          // Pick their highest-score entry (assume they beat you on this one)
          const target = rows.sort((a, b) => (b.score || 0) - (a.score || 0))[0];
          if (target && target.seed && !cancelled) {
            setNemesis({ ...enemy, seed: target.seed, seedScore: target.score, avatar: target.avatar });
          } else if (!cancelled) {
            setNemesis(enemy);
          }
        } catch (e) {
          if (!cancelled) setNemesis(enemy);
        }
      } catch (e) {
        if (!cancelled) setNemesis(null);
      }
    })();
    return () => { cancelled = true; };
  }, [playerName]);

  // Debounced stack lookup for the typed run code
  useEffect(() => {
    const code = codeInput.trim().toUpperCase();
    if (!code || code.length < 4) { setCodeStack(null); return; }
    let cancelled = false;
    setCodeStackLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/curses/${encodeURIComponent(code)}/stack`);
        if (!cancelled) setCodeStack(r.data);
      } catch (e) {
        if (!cancelled) setCodeStack({ count: 0, curses: [] });
      } finally {
        if (!cancelled) setCodeStackLoading(false);
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(t); };
  }, [codeInput]);

  const submitCode = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    try {
      const r = await axios.get(`${API}/runs/${code}`);
      if (r.data && r.data.seed) { beginClassSelect(r.data.seed, null, r.data.run_code || code); return; }
    } catch (e) { /* run code not found — fall through to raw seed */ }
    beginClassSelect(code, null, code);
  };

  return (
    <div className="relative min-h-screen w-full flex flex-col items-center justify-start sm:justify-center px-3 sm:px-6 py-4 sm:py-10 menu-root">
      {/* v3.6 layered ambient background: animated gradient + rainbow grid */}
      <div className="menu-bg-gradient" aria-hidden="true" />
      <div className="menu-bg-grid" aria-hidden="true" />
      <MenuAmbient />

      {/* Top-right util bar - desktop only. v3.20: explicit min-height + items-stretch so buttons don't shift */}
      <div className="hidden sm:flex absolute top-6 right-6 items-center gap-2 z-20 min-h-[36px]">
        <button className="brutal-btn text-xs px-3 py-2 h-[36px] flex items-center justify-center" onClick={toggleMute} data-testid="mute-toggle" title="Mute">
          {muted ? <VolumeX size={16}/> : <Volume2 size={16}/>}
        </button>
        <button className={`brutal-btn text-xs px-3 py-2 h-[36px] flex items-center gap-2 ${streamerMode ? 'brutal-btn-pink' : ''}`}
          onClick={toggleStreamerMode}
          data-testid="streamer-toggle"
          style={streamerMode ? {borderColor:'#ff007f',color:'#ff007f',boxShadow:'4px 4px 0 #ff007f'} : {}}>
          <Radio size={14} className={streamerMode ? 'animate-pulse' : ''}/> {streamerMode ? 'STREAMER ON' : 'STREAMER'}
        </button>
        {streamerMode && (
          <button
            className={`brutal-btn text-xs px-3 py-2 h-[36px] flex items-center gap-2 ${twitchStatus.connected ? 'brutal-btn-pink' : ''}`}
            onClick={() => setTwitchPanelOpen(v => !v)}
            data-testid="twitch-panel-toggle"
          >
            <Radio size={14} className={twitchStatus.connected ? 'animate-pulse' : ''}/>
            {twitchStatus.connected ? `#${twitchStatus.channel}` : 'CONNECT TWITCH'}
          </button>
        )}
        <button className="brutal-btn text-xs px-3 py-2 h-[36px] flex items-center gap-2" onClick={() => setScreen('profile')} data-testid="profile-button">
          <User size={14}/>
          {profile ? (
            <>
              <span>{profile.tag}</span>
              <Coins size={12} className="text-[color:var(--pink)]"/>
              <span className="text-[color:var(--pink)]">{profile.slop_tokens}</span>
            </>
          ) : 'PROFILE'}
        </button>
      </div>

      <div className="relative z-10 w-full max-w-4xl">
        {/* Title — v3.6 animated rainbow gradient wash */}
        <div className="text-center sm:text-left">
          <div className="mb-2 font-mono text-[10px] sm:text-xs uppercase tracking-[0.4em] text-[color:var(--text-dim)]">
            ROGUELIKE DECKBUILDER
          </div>
          <h1 className="font-display text-6xl sm:text-8xl font-black leading-[0.85] uppercase tracking-tighter menu-title-rainbow" data-testid="game-title">
            FRIEND
          </h1>
          <h1 className="font-display text-6xl sm:text-8xl font-black leading-[0.85] uppercase tracking-tighter menu-title-rainbow menu-title-rainbow-alt">
            SLOP
          </h1>
          <div className="mt-2 font-mono text-xs text-[color:var(--text-dim)]">v1.0.0 · GOONING TIME · build {process.env.REACT_APP_BUILD_HASH || 'DEV'}</div>
          {(runStreak > 0 || bestStreak > 0) && (
            <div className="mt-3 flex flex-wrap gap-2 justify-start" data-testid="menu-streak-row">
              {runStreak > 0 && (
                <span className="streak-badge" data-testid="menu-current-streak">
                  🔥 CURRENT × {runStreak}
                </span>
              )}
              {bestStreak > 0 && (
                <span className="brutal-border-dim px-2 py-1 text-xs font-bold uppercase text-[color:var(--pink)]" data-testid="menu-best-streak">
                  BEST × {bestStreak}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Mobile util bar — v3.20 compressed: single row, no wrapping, always visible tokens */}
        <div className="mt-3 flex sm:hidden items-center gap-2 flex-nowrap justify-center overflow-x-auto" data-testid="menu-mobile-utilbar">
          <button className="brutal-btn text-[11px] px-2.5 py-1.5 h-[32px] flex items-center gap-1 shrink-0" onClick={() => setScreen('profile')} data-testid="profile-button-mobile">
            <User size={12}/>
            {profile ? <><span className="truncate max-w-[70px]">{profile.tag}</span><Coins size={10} className="text-[color:var(--pink)]"/><span className="text-[color:var(--pink)]">{profile.slop_tokens}</span></> : 'PROFILE'}
          </button>
          <button className="brutal-btn text-[11px] px-2 py-1.5 h-[32px] flex items-center justify-center shrink-0" onClick={toggleMute} title="Mute" data-testid="mute-toggle-mobile">
            {muted ? <VolumeX size={14}/> : <Volume2 size={14}/>}
          </button>
          <button className={`brutal-btn text-[11px] px-2.5 py-1.5 h-[32px] flex items-center gap-1 shrink-0 ${streamerMode ? 'brutal-btn-pink' : ''}`}
            onClick={toggleStreamerMode}
            data-testid="streamer-toggle-mobile"
            style={streamerMode ? {borderColor:'#ff007f',color:'#ff007f'} : {}}>
            <Radio size={12}/> {streamerMode ? 'LIVE' : 'STREAM'}
          </button>
        </div>

        {/* Primary CTAs — big two-card row */}
        <div className="mt-4 sm:mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="brutal-border p-3 sm:p-5 bg-[#0a0a0a] brutal-shadow">
            <div className="flex items-center gap-3 mb-2">
              <button
                onClick={() => setScreen('avatar')}
                className="p-0 border-0 bg-transparent cursor-pointer"
                title="Customize avatar"
                data-testid="menu-avatar-chip-button"
              >
                <AvatarChip avatar={profile?.avatar} size={44} testid="menu-avatar-chip" />
              </button>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-dim)]">PLAYER TAG</div>
                {profile?.avatar?.title && (
                  <div className="text-[9px] uppercase font-mono text-[color:var(--pink)]" data-testid="menu-avatar-title">
                    THE {profile.avatar.title}
                  </div>
                )}
              </div>
            </div>
            <input
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value.slice(0, 16))}
              placeholder="ANON_GOON"
              className="w-full bg-black border-2 border-[color:var(--muted)] focus:border-[color:var(--neon)] px-3 py-2 text-base sm:text-lg font-mono uppercase outline-none"
              data-testid="player-name-input"
            />
            <button
              className="brutal-btn w-full mt-3 text-base sm:text-lg flex items-center justify-center gap-2"
              onClick={() => startQuickRun()}
              data-testid="start-run-button"
            >
              <Play size={18} /> START NEW RUN
            </button>
            {hasSavedRun && (
              <div className="mt-3 brutal-border-pink p-3 flex items-center gap-2" data-testid="continue-run-banner">
                <div className="flex-1 text-xs uppercase tracking-widest text-[color:var(--pink)]">
                  YOU HAVE A RUN IN PROGRESS
                </div>
                <button className="brutal-btn text-xs px-2 py-1" onClick={continueSavedRun} data-testid="continue-run-btn">
                  CONTINUE
                </button>
                <button className="brutal-btn text-xs px-2 py-1 text-[color:var(--red)]" onClick={discardSavedRun} data-testid="discard-run-btn">
                  DISCARD
                </button>
              </div>
            )}
          </div>

          <div className="brutal-border-pink p-5 bg-[#0a0a0a] brutal-shadow-pink">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-dim)] mb-2">RUN CODE (from a friend)</div>
            <input
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
              placeholder="ABC123"
              maxLength={12}
              className="w-full bg-black border-2 border-[color:var(--muted)] focus:border-[color:var(--pink)] px-3 py-2 text-lg font-mono uppercase outline-none tracking-widest"
              data-testid="run-code-input"
            />
            {/* v3.11 QoL: live curse-stack preview */}
            {codeInput.trim().length >= 4 && (
              <div className="mt-2 text-[10px] font-mono uppercase tracking-wider" data-testid="run-code-stack-preview">
                {codeStackLoading ? (
                  <span className="text-[color:var(--text-dim)]">CHECKING CURSES...</span>
                ) : codeStack && codeStack.count > 0 ? (
                  <span className="text-[color:var(--pink)]">
                    ⚠ {codeStack.count} CURSE{codeStack.count === 1 ? '' : 'S'} STACKED · {2 + Math.max(0, codeStack.count - 1) * 0.5}× TOKENS ON WIN
                  </span>
                ) : (
                  <span className="text-[color:var(--text-dim)]">UNCURSED · STANDARD PAYOUT</span>
                )}
              </div>
            )}
            <button
              className="brutal-btn-pink brutal-btn w-full mt-3 text-lg flex items-center justify-center gap-2"
              onClick={submitCode}
              data-testid="enter-run-code-button"
            >
              <Hash size={18} /> RUN THIS SEED
            </button>
          </div>
        </div>

        {/* v3.12 NEMESIS — top rival who beat you; one-click AVENGE run */}
        {nemesis && (
          <div
            className="mt-4 brutal-border-dim bg-[#160a10] p-3 flex items-center justify-between gap-3 flex-wrap"
            style={{ borderColor: '#ff2b2b', boxShadow: '4px 4px 0 #ff2b2b' }}
            data-testid="menu-nemesis-panel"
          >
            <div className="flex items-center gap-3 min-w-0">
              {nemesis.avatar && (nemesis.avatar.emoji || nemesis.avatar.color) && (
                <AvatarChip avatar={nemesis.avatar} size={34} testid="menu-nemesis-avatar" />
              )}
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--red)] font-mono">
                  ⚡ NEMESIS · BEAT YOU {nemesis.losses}×
                </div>
                <div className="font-display font-black uppercase text-lg truncate" data-testid="menu-nemesis-tag">
                  {nemesis.tag}
                </div>
                <div className="text-[10px] uppercase text-white/60 font-mono" data-testid="menu-nemesis-score">
                  BEST SCORE · <span className="text-[color:var(--pink)]">{nemesis.seedScore || nemesis.best_score}</span>
                  {nemesis.seed && <span className="ml-2 text-[color:var(--text-dim)]">SEED · <span className="text-white">{nemesis.seed}</span></span>}
                </div>
              </div>
            </div>
            {nemesis.seed && (
              <button
                className="brutal-btn text-sm px-3 py-2"
                style={{ borderColor: '#ff2b2b', color: '#ff2b2b', boxShadow: '3px 3px 0 #ff2b2b' }}
                onClick={() => beginClassSelect(nemesis.seed)}
                data-testid="menu-nemesis-avenge"
              >
                ⚔ AVENGE
              </button>
            )}
          </div>
        )}

        {/* Big PVP CTA */}
        <button
          className="mt-4 w-full brutal-btn text-lg py-3 flex items-center justify-center gap-3 pvp-cta-btn"
          onClick={() => setScreen('party')}
          data-testid="pvp-menu-cta"
          style={{ borderColor: 'var(--pink)', color: 'var(--pink)', boxShadow: '6px 6px 0 var(--pink)' }}
        >
          <Users size={20} /> PLAY WITH FRIENDS
          <PartyLiveTicker />
        </button>

        {/* Featured buttons - Daily / Weekly / Room */}
        <div className="mt-3 grid grid-cols-3 gap-3">
          <button className="brutal-btn text-sm flex items-center justify-center gap-2 menu-section-btn"
            onClick={() => setScreen('daily')}
            data-testid="daily-challenge-button"
            style={{ borderColor: '#ff007f', color: '#ff007f', boxShadow: '4px 4px 0 #ff007f', ['--hue']: '#ff007f' }}>
            <Calendar size={16} /> DAILY
          </button>
          <button className="brutal-btn text-sm flex items-center justify-center gap-2 menu-section-btn"
            onClick={() => setScreen('weeklybrawl')}
            data-testid="weekly-brawl-button"
            style={{ borderColor: '#ffd700', color: '#ffd700', boxShadow: '4px 4px 0 #ffd700', ['--hue']: '#ffd700' }}>
            <Trophy size={16} /> WEEKLY
          </button>
          <button className="brutal-btn text-sm flex items-center justify-center gap-2 menu-section-btn"
            onClick={() => setScreen('party')}
            data-testid="party-mode-button"
            style={{ borderColor: '#00e5ff', color: '#00e5ff', boxShadow: '4px 4px 0 #00e5ff', ['--hue']: '#00e5ff' }}>
            <Users size={16} /> ROOM
          </button>
        </div>

        {/* MORE — v3.6 rainbow tinted */}
        <div className="mt-3">
          <button
            className="w-full brutal-btn text-xs px-3 py-2 flex items-center justify-center gap-2"
            onClick={() => setMoreOpen(o => !o)}
            data-testid="menu-more-toggle"
          >
            MORE MODES {moreOpen ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
          </button>
          {moreOpen && (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="menu-more-panel">
              <SectionBtn hue={SECTION_HUES.perks}       testid="perks-button"           onClick={() => setScreen('perks')}        icon={Star}       label="PERKS" />
              <SectionBtn hue={SECTION_HUES.draft}       testid="draft-mode-button"      onClick={() => setScreen('draft')}        icon={Shuffle}    label="DRAFT" />
              <SectionBtn hue={SECTION_HUES.marketplace} testid="marketplace-button"     onClick={() => setScreen('marketplace')}  icon={Store}      label="MARKET" />
              <SectionBtn hue={SECTION_HUES.bounties}    testid="bounties-button"        onClick={() => setScreen('bounties')}     icon={Trophy}     label="BOUNTIES" />
              <SectionBtn hue={SECTION_HUES.codex}       testid="codex-button"           onClick={() => setScreen('codex')}        icon={BookOpen}   label="CODEX" />
              <SectionBtn hue={SECTION_HUES.avatar}      testid="avatar-button"          onClick={() => setScreen('avatar')}       icon={User}       label="AVATAR" />
              <SectionBtn hue={SECTION_HUES.mastery}     testid="mastery-button"         onClick={() => setScreen('mastery')}      icon={Sparkles}   label="MASTERY" />
              <SectionBtn hue={SECTION_HUES.achievements}testid="achievements-button"   onClick={() => setScreen('achievements')} icon={Award}      label="ACHIEVES" />
              <SectionBtn hue={SECTION_HUES.leaderboard} testid="view-leaderboard-button"onClick={() => setScreen('leaderboard')}  icon={Trophy}     label="BOARD" />
              <SectionBtn hue={SECTION_HUES.cursed}      testid="cursed-runs-button"     onClick={() => { setLeaderboardInitialTab('cursed'); setScreen('leaderboard'); }} icon={Skull}      label="CURSED" />
              <SectionBtn hue={SECTION_HUES.settings}    testid="settings-button"        onClick={() => setScreen('settings')}     icon={Settings}   label="SETTINGS" />
              <SectionBtn hue={SECTION_HUES.howto}       testid="how-to-play-button"     onClick={() => setScreen('howto')}        icon={HelpCircle} label="HELP" />
            </div>
          )}
        </div>

        {topScore && (
          <div className="mt-8 border-t-2 border-[color:var(--muted)] pt-4 flex items-center justify-between text-xs">
            <span className="text-[color:var(--text-dim)] uppercase tracking-widest">Current king of slop</span>
            <span className="font-display uppercase font-bold text-[color:var(--neon)]" data-testid="top-scorer">
              {topScore.player_name} — {topScore.score}
            </span>
          </div>
        )}
      </div>

      {/* Twitch chat-vote settings modal */}
      {twitchPanelOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" data-testid="twitch-panel">
          <div className="brutal-border-pink bg-[#0a0a0a] p-6 max-w-md w-full mx-4">
            <div className="text-xs uppercase tracking-[0.4em] text-[color:var(--pink)]">TWITCH CHAT VOTE</div>
            <h3 className="font-display font-black text-2xl uppercase mt-2 text-[color:var(--pink)]">CONNECT YOUR CHANNEL</h3>
            <div className="text-xs text-[color:var(--text-dim)] mt-2">
              Anonymous mode — no login needed. Chat types <b>!1 !2 !3</b> during card rewards. Highest vote wins after 20s.
            </div>
            <input
              className="mt-4 w-full bg-black brutal-border px-3 py-2 font-mono uppercase"
              placeholder="your_twitch_channel"
              value={twitchInput}
              onChange={(e) => setTwitchInput(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
              data-testid="twitch-channel-input"
            />
            {twitchStatus.error && (
              <div className="mt-2 text-xs text-[color:var(--red)]" data-testid="twitch-error">{twitchStatus.error}</div>
            )}
            <div className="mt-4 flex gap-3">
              {!twitchStatus.connected ? (
                <button className="brutal-btn brutal-btn-pink flex-1" data-testid="twitch-connect-btn"
                  onClick={async () => { await connectTwitch(twitchInput); }}>
                  CONNECT
                </button>
              ) : (
                <button className="brutal-btn flex-1" data-testid="twitch-disconnect-btn"
                  onClick={async () => { await disconnectTwitch(); }}>
                  DISCONNECT
                </button>
              )}
              <button className="brutal-btn" onClick={() => setTwitchPanelOpen(false)} data-testid="twitch-close-btn">
                CLOSE
              </button>
            </div>
            {twitchStatus.connected && (
              <div className="mt-3 text-xs text-[color:var(--neon)]" data-testid="twitch-connected-msg">
                ✓ LIVE ON #{twitchStatus.channel}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// v1.2 MAX LIFE: drifting brainrot glyphs behind the menu (kept as ambience)
const AMBIENT_GLYPHS = ['💀','🌀','🧠','💅','🔥','😭','🥶','🫠','⚡','🩸','👁','🤡','🍜','🩻','🦴','🕳'];
const AMBIENT_COLORS = ['#39ff14','#ff007f','#00e5ff','#ffe74a','#a0ff2e','#ff2ea6','#7cd3fc','#c084fc'];
function MenuAmbient() {
  const particles = React.useMemo(() => Array.from({ length: 22 }, (_, i) => ({
    id: i,
    glyph: AMBIENT_GLYPHS[Math.floor(Math.random() * AMBIENT_GLYPHS.length)],
    color: AMBIENT_COLORS[Math.floor(Math.random() * AMBIENT_COLORS.length)],
    left: Math.random() * 100,
    dur: 14 + Math.random() * 22,
    delay: Math.random() * -25,
    driftX: Math.random() * 2 - 1,
    size: 24 + Math.random() * 44,
  })), []);
  return (
    <div className="menu-ambient" aria-hidden="true">
      {particles.map(p => (
        <span
          key={p.id}
          className="menu-glyph"
          style={{
            left: `${p.left}%`,
            color: p.color,
            fontSize: `${p.size}px`,
            animationDuration: `${p.dur}s`,
            animationDelay: `${p.delay}s`,
            ['--drift-x']: p.driftX,
          }}
        >{p.glyph}</span>
      ))}
    </div>
  );
}
